import React, { useEffect, useMemo, useState } from 'react';
import { ServicesProps, LineItemProps } from '@jbhi-fi/apis/src/services/types';
import { ProductServiceButton } from '@jbhi-fi/components/src/Attach/ProductServiceButton/ProductServiceButton';
import { ServiceIcon } from '../productService/icons/ServiceIcon';
import { initPDPServiceSelections } from '../utils/product-service';
import { buildInstallSuggestionResult } from './utils';
import { useInstallServiceDrawer } from './InstallServiceDrawerContext';
import { styles } from './installProductService.css';
import type { InstallProductServiceProps } from './types';
import { InstallContent } from './InstallContent';
import { getCartLoading } from '../../../../utils/cart/cartUtils';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';

export const InstallProductService = ({
  productSku,
  productServiceLink,
  facets,
  suggestions,
  preSelectedOfferingId,
  variant = 'slideout',
  productLineId,
  onAddonSelectionChange,
}: InstallProductServiceProps) => {
  const [isSelected] = useState(false);
  const installServiceDrawer = useInstallServiceDrawer();
  const gtmSource = variant === 'cart' ? 'cart' : variant === 'minicart' ? 'minicart' : 'slideout';
  const isSlideout = variant === 'slideout';
  const eventType = isSlideout ? 'TV Install Modal' : 'TV Install Tile';
  const isCartOrMiniCart = variant === 'minicart' || variant === 'cart';
  const isDesktop = useBreakpoint('md', 'up');
  const tvInstallMetafield = window?.Jbhifi?.installConfig?.tvInstallConfig?.tvInstallMetaField;

  const { currentSuggestion } = useMemo(
    () =>
      buildInstallSuggestionResult({
        suggestions,
        productSku,
        tvInstallGroups: tvInstallMetafield?.tvInstallGroups,
      }),
    [suggestions, productSku, tvInstallMetafield?.tvInstallGroups]
  );

  const service = useMemo<ServicesProps | undefined>(() => {
    return currentSuggestion?.services?.[0];
  }, [currentSuggestion]);

  useEffect(() => {
    if (['pdp', 'compare'].includes(variant)) {
      initPDPServiceSelections();
    }
  }, [variant]);

  const handleModalCloseRequest = () => {
    if (getCartLoading()) return false;
    return true;
  };

  const openInstallDrawer = () => {
    if (!installServiceDrawer || !productLineId) return;

    installServiceDrawer.openDrawer({
      productLineId: productLineId,
      content: (
        <InstallContent
          key={productLineId}
          productLineId={productLineId}
          productSku={productSku}
          productServiceLink={productServiceLink}
          variant={variant}
          gtmSource={gtmSource}
          eventType={eventType}
          suggestions={suggestions as Array<LineItemProps>}
          preSelectedOfferingId={preSelectedOfferingId}
          facets={facets}
        />
      ),
      onBeforeClose: handleModalCloseRequest,
    });
  };

  useEffect(() => {
    if (!installServiceDrawer || !isCartOrMiniCart || !productLineId) return;

    installServiceDrawer.setContent(
      productLineId,
      <InstallContent
        key={productLineId}
        productLineId={productLineId}
        productSku={productSku}
        productServiceLink={productServiceLink}
        variant={variant}
        gtmSource={gtmSource}
        eventType={eventType}
        suggestions={suggestions as Array<LineItemProps>}
        preSelectedOfferingId={preSelectedOfferingId}
        facets={facets}
        onAddonSelectionChange={onAddonSelectionChange}
      />
    );
    // Avoid endless loop
  }, [productLineId, productSku, productServiceLink, suggestions, preSelectedOfferingId]);

  if (isCartOrMiniCart) {
    return (
      <div className={styles.minicartWrapper}>
        <div className={styles.productServiceButtonWrapper}>
          <ProductServiceButton
            onButtonClick={openInstallDrawer}
            isCartPage={variant === 'cart' && isDesktop}
            isCompare={false}
            offeringId=""
            elementId={variant}
            selected={isSelected}
            heading="TV install: Setup & connect on cabinet or wall mount"
            icon={<ServiceIcon imageUrl={service?.offerings?.[0]?.imageUrl} selected={isSelected} size={42} />}
          />
        </div>
      </div>
    );
  }

  return (
    <InstallContent
      key={productLineId}
      productLineId={productLineId}
      productSku={productSku}
      productServiceLink={productServiceLink}
      variant={variant}
      gtmSource={gtmSource}
      eventType={eventType}
      suggestions={suggestions as Array<LineItemProps>}
      preSelectedOfferingId={preSelectedOfferingId}
      facets={facets}
      onAddonSelectionChange={onAddonSelectionChange}
    />
  );
};
